import React, { FC, useCallback, useEffect, useState } from 'react'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { CaptionXS, Text, TitleXS } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { useToast } from '@/molecules/Toast'
import { SubmitNameForCreditsMutation, SubmitNameForCreditsMutationVariables } from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'

export const SUBMIT_NAME_FOR_CREDITS = gql`
  mutation SubmitNameForCredits($input: SubmitNameForCreditsInput!) {
    submitNameForCredits(input: $input) {
      nameInCredits
    }
  }
`

interface Props {
  isOpen: boolean
  onClose: () => void
  orderId: string
  theatricalSlug: string
  nameInCredits: string
  onSuccess: () => void
}

export const NameInCreditsFormModal: FC<Props> = ({
  isOpen,
  onClose,
  orderId,
  theatricalSlug,
  nameInCredits,
  onSuccess,
}) => {
  const { t } = useTranslate('common')
  const [inputValue, setInputValue] = useState(nameInCredits)
  const [errorMessage, setErrorMessage] = useState('')
  const { showToast } = useToast()

  const [submitNameForCredits, { loading }] = useMutation<
    SubmitNameForCreditsMutation,
    SubmitNameForCreditsMutationVariables
  >(SUBMIT_NAME_FOR_CREDITS, {
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (nameInCredits) setInputValue(nameInCredits)
  }, [nameInCredits])

  const handleCreditNameSubmit = useCallback(
    async (e: React.SyntheticEvent<HTMLFormElement>) => {
      try {
        e.preventDefault()
        if (loading) return
        if (!inputValue) return setErrorMessage(t('nameCantBeEmpty', "Name can't be empty"))

        const { data } = await submitNameForCredits({
          variables: {
            input: {
              projectSlug: theatricalSlug,
              orderId,
              requestedName: inputValue,
              includeInTheatricalCredits: true,
            },
          },
        })

        if (data?.submitNameForCredits?.nameInCredits) {
          showToast(t('yourNameHasBeenSubmitted', 'Your name has been submitted!'))
          onSuccess()
          return onClose()
        }
      } catch (err) {
        logger().error('SubmitNameForCreditsFormModal failed during submit', err)
      }
    },
    [inputValue, loading, onClose, onSuccess, orderId, showToast, submitNameForCredits, t, theatricalSlug],
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="mx-4"
      childWrapperClassName="!m-0 !p-0"
      panelClasses="!w-full !bg-white !p-6 !pt-8 md:!p-8 md:!pt-10 !max-w-[358px] md:!max-w-[580px]"
    >
      <FlexColumn className="gap-6">
        <button
          className="absolute right-4 top-4 cursor-pointer rounded-full bg-core-gray-900 p-2 md:right-6 md:top-6"
          onClick={onClose}
        >
          <CloseIcon color="white" size={16} />
        </button>

        <div className="space-y-2">
          <Text className="photon-title-md md:photon-title-lg block" weight="bold">
            {t('yourNameInTheCreditsTitle', 'Your Name in the Credits')}
          </Text>

          <Text className="photon-paragraph-md md:photon-paragraph-lg block" weight="medium">
            {t('yourNameInTheCreditsDescription', 'Enter or edit your name that will display in the credits.')}
          </Text>
        </div>

        <div className="space-y-2">
          <TitleXS weight="semibold">{t('deadlines', 'Deadlines')}:</TitleXS>
          <ul className="list-inside list-disc px-4 text-core-gray-700">
            <li>
              <Text color="core-gray-700" className="photon-paragraph-sm md:photon-paragraph-md" weight="medium">
                {t('yourNameInTheCreditsListOne', 'Before Nov. 15th to be added to the theater release.')}
              </Text>
            </li>
            <li>
              <Text color="core-gray-700" className="photon-paragraph-sm md:photon-paragraph-md" weight="medium">
                {t('yourNameInTheCreditsListTwo', 'After Nov. 15th to be added to the streaming release.')}
              </Text>
            </li>
          </ul>
        </div>

        <form className="flex w-full flex-col gap-6" onSubmit={handleCreditNameSubmit}>
          <div className="relative flex flex-col">
            <input
              className={classNames(
                'photon-caption-lg rounded-lg border bg-core-gray-100 px-4 py-3 font-medium',
                errorMessage ? 'border-red' : 'border-core-gray-200',
              )}
              type="text"
              value={inputValue}
              disabled={loading}
              placeholder={t('yourNameInTheCreditsInputPlaceholder', 'Enter your name here')}
              onChange={(e) => {
                setErrorMessage('')
                setInputValue(e.target.value)
              }}
            />
            <CaptionXS className="absolute left-0.5 top-full pt-0.5" color="red">
              {errorMessage}
            </CaptionXS>
          </div>

          <Button type="submit" disabled={!inputValue} className="flex items-center gap-1 md:mr-auto" variant="black">
            {loading && <LoadingSpinnerIcon size={16} color="white" />}
            {t('submit', 'Submit')}
          </Button>
        </form>
      </FlexColumn>
    </Modal>
  )
}
